<!--
/*
 * Компонент OtpCodeInput — переиспользуемый ввод 6-значного OTP-кода
 */
-->
<script setup lang="ts">
import { ref, watch } from 'vue'
import { useIntervalFn } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

function useCooldown(seconds = 60) {
  const remaining = ref(0)
  const { pause, resume } = useIntervalFn(
    () => {
      remaining.value--
      if (remaining.value <= 0) pause()
    },
    1000,
    { immediate: false },
  )
  const start = () => {
    remaining.value = seconds
    resume()
  }
  return { remaining, start }
}

interface IOtpCodeInputProps {
  length?: number
  loading?: boolean
  error?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<IOtpCodeInputProps>(), {
  length: 6,
  loading: false,
  error: '',
  disabled: false,
})

const emit = defineEmits<{
  complete: [code: string]
  resend: []
}>()

const { t } = useI18n()

const otp = ref('')
const { remaining: resendTimer, start: startResendTimer } = useCooldown(60)

const onFinish = (code: string) => {
  emit('complete', code)
}

const handleResend = () => {
  startResendTimer()
  emit('resend')
}

startResendTimer()

watch(
  () => props.error,
  (newError) => {
    if (newError) {
      otp.value = ''
    }
  },
)
</script>

<template>
  <div class="d-flex flex-column ga-4 align-center">
    <span class="text-caption text-medium-emphasis text-center">
      {{ t('auth.otpHint') }}
    </span>

    <v-otp-input
      v-model="otp"
      :length="props.length"
      :loading="props.loading"
      :disabled="props.disabled || props.loading"
      type="number"
      @finish="onFinish"
    />

    <v-alert
      v-if="props.error"
      type="error"
      variant="tonal"
      density="compact"
      class="text-caption w-100"
    >
      {{ props.error }}
    </v-alert>

    <v-btn
      variant="text"
      color="primary"
      size="small"
      class="resend-btn"
      :disabled="resendTimer > 0 || props.loading"
      @click="handleResend"
    >
      {{ resendTimer > 0 ? t('auth.resendCodeCount', { seconds: resendTimer }) : t('auth.resendCode') }}
    </v-btn>
  </div>
</template>

<style scoped lang="scss">
:deep(.v-btn.v-btn--disabled) {
  color: rgb(var(--v-theme-text-disabled)) !important;
  opacity: 0.85 !important;

  .v-btn__content {
    color: rgb(var(--v-theme-text-disabled)) !important;
  }
}
</style>
